import ImagePlaceholderTsoPq from '@components/ImagePlaceholder/ImagePlaceholderTsoPq';
import LegendTsoPq from '@components/Legend/LegendTsoPq';
import SplitButtonMenuTsoPq, { ButtonConfig } from '@components/SplitButtonMenu/SplitButtonMenuTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { Box } from '@mui/material';
import { useAppSelector } from '@redux/hooks';
import {
  selectDataGridSelectedDimensions,
  selectAppliedParameters,
  setDataGridSelectedDimensions,
} from '@redux/slices/pqIndexSlice';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  StyledGridContainer,
  StyledLegendContainer,
  StyledPqIndexContent,
  StyledSplitButtonMenuTsoPqWrapper,
  StyledTitleWrapper,
} from '../PqIndexStyle';
import PqIndexDataGrid from './PqIndexDataGrid';

const PqIndexApplyedParameters = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const parameters = useAppSelector(selectAppliedParameters);
  const dataGridDimensions = useAppSelector(selectDataGridSelectedDimensions);

  const measurementOptions = [
    t('pqIndex.segmenteButtonMenu.measurement.substation'),
    t('pqIndex.segmenteButtonMenu.measurement.measurementPoint'),
    t('pqIndex.segmenteButtonMenu.measurement.measurementDevice'),
  ];
  const parameterCategoryOptions = [
    t('pqIndex.segmenteButtonMenu.parameterCategory.measurementParameterCategory'),
    t('pqIndex.segmenteButtonMenu.parameterCategory.measurementParameter'),
  ];
  const timespanOptions = [
    t('pqIndex.segmenteButtonMenu.timespan.totalPeriod'),
    t('pqIndex.segmenteButtonMenu.timespan.years'),
    t('pqIndex.segmenteButtonMenu.timespan.months'),
    t('pqIndex.segmenteButtonMenu.timespan.calendarWeeks'),
  ];

  const buttonsConfig: ButtonConfig[] = [
    {
      id: 'measurement',
      label: t('pqIndex.segmenteButtonMenu.measurement.substation'),
      options: measurementOptions,
      customWidth: '13.75rem',
    },
    {
      id: 'parameterCategory',
      label: t('pqIndex.segmenteButtonMenu.parameterCategory.measurementParameterCategory'),
      options: parameterCategoryOptions,
      customWidth: '19.25rem',
    },
    {
      id: 'timespan',
      label: t('pqIndex.segmenteButtonMenu.timespan.totalPeriod'),
      options: timespanOptions,
      customWidth: '11.625rem',
    },
  ];

  const handleChange = (values: { [key: string]: any }) => {
    dispatch(setDataGridSelectedDimensions(values));
  };

  useEffect(() => {
    const translateStoredValue = (
      value: string | undefined,
      translationKeys: string[]
    ): string | undefined => {
      if (!value) {
        return undefined;
      }

      const englishLabels = translationKeys.map((key) => i18n.getFixedT('en')(key));
      const germanLabels = translationKeys.map((key) => i18n.getFixedT('de')(key));
      const currentLabels = translationKeys.map((key) => t(key));

      const valueIndex = [...englishLabels, ...germanLabels].indexOf(value);

      if (valueIndex === -1) {
        return value;
      }

      return currentLabels[valueIndex % translationKeys.length];
    };

    const normalizedDimensions = {
      measurement: translateStoredValue(dataGridDimensions.measurement, [
        'pqIndex.segmenteButtonMenu.measurement.substation',
        'pqIndex.segmenteButtonMenu.measurement.measurementPoint',
        'pqIndex.segmenteButtonMenu.measurement.measurementDevice',
      ]),
      timespan: translateStoredValue(dataGridDimensions.timespan, [
        'pqIndex.segmenteButtonMenu.timespan.totalPeriod',
        'pqIndex.segmenteButtonMenu.timespan.years',
        'pqIndex.segmenteButtonMenu.timespan.months',
        'pqIndex.segmenteButtonMenu.timespan.calendarWeeks',
      ]),
      parameterCategory: translateStoredValue(dataGridDimensions.parameterCategory, [
        'pqIndex.segmenteButtonMenu.parameterCategory.measurementParameterCategory',
        'pqIndex.segmenteButtonMenu.parameterCategory.measurementParameter',
      ]),
    };

    if (
      normalizedDimensions.measurement !== dataGridDimensions.measurement ||
      normalizedDimensions.timespan !== dataGridDimensions.timespan ||
      normalizedDimensions.parameterCategory !== dataGridDimensions.parameterCategory
    ) {
      dispatch(setDataGridSelectedDimensions(normalizedDimensions));
    }
  }, [
    dataGridDimensions.measurement,
    dataGridDimensions.parameterCategory,
    dataGridDimensions.timespan,
    dispatch,
    i18n,
    t,
  ]);

  return (
    <StyledPqIndexContent>
      <StyledTitleWrapper>
        <TypographyTsoPq typographyType={'pageTitleBold'}>{t('pqIndex.title')}</TypographyTsoPq>
        <TypographyTsoPq typographyType={'pageSubtitleSM'}>{t('pqIndex.subTitle')}</TypographyTsoPq>
      </StyledTitleWrapper>
      {parameters.tsos.length > 0 ? (
        <StyledGridContainer>
          <Box>
            <StyledSplitButtonMenuTsoPqWrapper>
              <SplitButtonMenuTsoPq
                handleChange={handleChange}
                buttonsConfig={buttonsConfig}
                defaultSelectedOptions={{
                  measurement:
                    dataGridDimensions.measurement ?? t('pqIndex.segmenteButtonMenu.measurement.measurementDevice'),
                  timespan: dataGridDimensions.timespan ?? t('pqIndex.segmenteButtonMenu.timespan.calendarWeeks'),
                }}
              ></SplitButtonMenuTsoPq>
            </StyledSplitButtonMenuTsoPqWrapper>
            <StyledLegendContainer>
              <LegendTsoPq />
            </StyledLegendContainer>
          </Box>
          <PqIndexDataGrid />
        </StyledGridContainer>
      ) : (
        <ImagePlaceholderTsoPq
          title={t('pqIndex.segmenteButtonMenu.noParameterSelected.title')}
          description={t('pqIndex.segmenteButtonMenu.noParameterSelected.subtitle')}
        />
      )}
    </StyledPqIndexContent>
  );
};

export default PqIndexApplyedParameters;
